// @ts-nocheck
import { MouseEvent, ReactNode, useContext, useEffect, useState } from 'react'

import { CardAuthor, CardReactions, PostContent } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { Heart, Comment } from '@hhgtech/icons/other'
import {
  Box,
  BoxProps,
  createStyles,
  rem,
  useMantineTheme,
} from '@mantine/core'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import {
  getHelloSitesUrl,
  getUrlToRedirectToSSOWithReturnUrl,
} from 'api/utils/paths'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { IS_SSOV2_ENABLED } from 'interfaces/constants'
import { TogetherComment, TogetherPost } from 'interfaces/types'
import { AppContext } from 'state/appContext'

export enum CardReactionType {
  post = 1,
  reply = 2,
}

type CardPostProps = {
  post: TogetherPost
  comment?: TogetherComment
  hasExpertComment?: boolean
  children?: ReactNode
} & BoxProps

const useStyles = createStyles(() => {
  return {
    postContent: {
      position: 'relative',
    },
    linkOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  }
})

const _CardPostItem = ({
  post,
  comment,
  children,
  hasExpertComment,
  ...rest
}: CardPostProps) => {
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { locale } = useRouterExtend()
  const theme = useMantineTheme()
  const { classes } = useStyles()
  const { formatMessage: f } = useIntl()

  const community = post?.community
  const author = post?.author
  const name = author?.name || author?.username || ''
  const username = author?.username || author?.name || ''
  const avatar = author?.avatar
  const authorRole = post?.isAnonymous ? undefined : author?.role
  const authorProps = {
    isAnonymous: post?.isAnonymous,
    avatar: avatar,
    name: name,
    username: username,
    email: author?.email,
    timeText: post?.createdTime,
    time: post?.scheduleDate || post?.createdDate,
    community: community,
    // miniIcon="./svg/icon-tag.svg"
    className: `post-author with-popover`,
    role: authorRole,
    title: author?.title,
    userId: author?.id,
    expertId: String(post?.author?.partnerId),
    //   dataEventCategory: TRACKING_CATEGORY,
    //   dataEventAction: 'Writer Profile Click',
    //   dataEventLabel: getCurrentHref(locale, asPath),
    'data-testid': 'name',
  } as any
  const [isLike, setIsLike] = useState(post.liked)
  const [isTrigger, setIsTrigger] = useState(false)
  const handleLikeClick = async () => {
    if (!isLoggedIn) {
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=${encodeURIComponent(
            `/${community?.slug}/${post?.slug}?postAction=like`,
          )}`,
        })
      } else {
        window.open(
          getUrlToRedirectToSSOWithReturnUrl(
            `${getHelloSitesUrl(
              locale,
            )}/community/on-boarding/?returnUrl=${encodeURIComponent(
              `/${community?.slug}/${post?.slug}?postAction=like`,
            )}`,
          ),
        )
      }
    } else {
      await callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_LIKE_POST,
        {
          params: {
            postId: post.id,
          },
        },
        'together',
      )
      setIsLike((pre) => !pre)
      setIsTrigger(true)
    }
  }
  const [likeCount, setLikeCount] = useState<number>(post?.likesCount)
  const staticUrl = `/community/${community.slug}/${post?.slug}`
  const onClickPost = (e?: MouseEvent<HTMLAnchorElement>, hash?: string) => {
    e?.preventDefault()
    if (!isLoggedIn) {
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=${encodeURIComponent(
            `/${community.slug}/${post?.slug}${hash}`,
          )}`,
        })
      } else {
        window.location.href = getUrlToRedirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
            community.slug
          }/${post?.slug}?source=communities&action=embeddedarticle&campaign=${
            community.slug
          }/${post?.slug}${hash}`,
        )
      }
    } else {
      window.open(`${staticUrl}${hash}`)
    }
  }

  useEffect(() => {
    if (isTrigger) {
      isLike ? setLikeCount((pre) => ++pre) : setLikeCount((pre) => --pre)
    }
  }, [post?.likesCount, isTrigger, isLike])
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: rem(12),
        border: `1px solid ${theme.colors.neutral[1]}`,
        padding: rem(24),
        borderRadius: rem(8),
        justifyContent: 'space-between',
        [theme.fn.smallerThan('md')]: { padding: rem(16) },
      })}
      {...rest}
    >
      <Box
        sx={() => ({
          display: 'flex',
          flexDirection: 'column',
          gap: rem(12),
        })}
      >
        <CardAuthor {...authorProps}>
          <CardAuthor.MainText>
            {hasExpertComment ? (
              <a href={staticUrl} title={post.title} onClick={onClickPost}>
                {post?.title}
              </a>
            ) : (
              name
            )}
          </CardAuthor.MainText>
          <CardAuthor.SubTextRow>
            {hasExpertComment ? (
              <>
                <CardAuthor.SubText>{post?.author?.name}</CardAuthor.SubText>
                <CardAuthor.SubDivider />
                <CardAuthor.SubText>{post?.createdTime}</CardAuthor.SubText>
              </>
            ) : (
              <CardAuthor.SubText>{post?.createdTime}</CardAuthor.SubText>
            )}
          </CardAuthor.SubTextRow>
        </CardAuthor>

        {!hasExpertComment && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: rem(8),
            }}
          >
            {!comment && post?.title && (
              <a href={staticUrl} title={post?.title} onClick={onClickPost}>
                <Text
                  size="s3"
                  as="h4"
                  weight="semiBold"
                  color={theme.colors.gray[8]}
                >
                  {post.title}
                </Text>
              </a>
            )}
            <Text size="p3" as="div" className={classes.postContent}>
              <PostContent
                key={String(post?.id)}
                // trackingCategory={TRACKING_CATEGORY}
                // url={postHref}
                className="post-content-wrapper"
                description={comment?.content || post?.description}
                //   disableShortenContent={disableShortenContent}
                mentions={post?.mentions}
                data-testid="Content of the Post"
                maxChars={200}
                style={{ padding: 0 }}
                hideShowAllBtn
                //   onClickCreatePostLink={handleClickCreatePost}
              />
              <a
                className={classes.linkOverlay}
                href={staticUrl}
                title={post?.title}
                onClick={onClickPost}
              >
                &nbsp;
              </a>
            </Text>
          </Box>
        )}
        {children}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignContent: 'center',
        }}
      >
        {!hasExpertComment ? (
          <CardReactions
            // postUrl={postRelateiveHref}
            //   trackingCategory={TRACKING_CATEGORY}
            //   trackingLabel={postHref}
            type={CardReactionType.post}
            loveCount={post.likeCount}
            replyCount={post.commentCount}
            //   className={`post-reaction ${
            //     photos?.length || previewUrl?.url ? 'hide-top-border' : ''
            //   }`}
            liked={post?.liked}
            onCommentClick={() => {
              onClickPost(undefined, '#comment')
            }}
            onLikeClick={handleLikeClick}
          >
            <Box
              sx={{
                display: 'flex',
                gap: rem(24),
              }}
            >
              <CardReactions.LikeWrapper>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                  }}
                >
                  <Heart
                    size={20}
                    isSolid={isLike}
                    color={isLike ? theme.colors.red[8] : undefined}
                    className="txt-icon"
                  />
                  <Text size="p3">{likeCount}</Text>
                </Box>
              </CardReactions.LikeWrapper>
              <CardReactions.CommentWrapper>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                  }}
                >
                  <Comment size={18} />
                  <Text size="p3">{post.commentsCount}</Text>
                </Box>
              </CardReactions.CommentWrapper>
            </Box>
          </CardReactions>
        ) : (
          <span>&nbsp;</span>
        )}
        <a href={staticUrl} title={post.title} onClick={onClickPost}>
          <Text size="p4" color={theme.fn.primaryColor()}>
            {f({ id: 'expert.cardPost.viewDetail' })}
          </Text>
        </a>
      </Box>
    </Box>
  )
}

export const _CardComment = ({ post }: CardPostProps) => {
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { classes } = useStyles()
  const answer = post?.expertAnswer?.answer?.[0] || {}
  const community = post?.community
  const author = post?.expertAnswer?.expert
  const name = author?.name || author?.username || ''
  const username = author?.username || author?.name || ''
  const avatar = author?.avatar
  const authorRole = post?.isAnonymous ? undefined : author?.role
  const authorProps = {
    isAnonymous: answer?.isAnonymous,
    avatar: avatar,
    name: name,
    username: username,
    //   url:
    //     author?.email === userInfo?.email
    //       ? SSO_URL + DISCOVER_PATHS.GET_PROFILE
    //       : '',
    email: author?.email,
    timeText: post?.createdTime,
    time: post?.scheduleDate || post?.createdDate,
    community: community,
    // miniIcon="./svg/icon-tag.svg"
    className: `post-author with-popover`,
    role: authorRole,
    title: author?.title as any,
    userId: author?.id,
    expertId: String(post?.author?.partnerId),
    //   dataEventCategory: TRACKING_CATEGORY,
    //   dataEventAction: 'Writer Profile Click',
    //   dataEventLabel: getCurrentHref(locale, asPath),
    'data-testid': 'name',
  } as any
  const staticUrl = `/community/${community.slug}/${post?.slug}`
  const onClickPost = (e?: MouseEvent<HTMLAnchorElement>, hash?: string) => {
    if (!isLoggedIn) {
      e?.preventDefault()
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=${encodeURIComponent(
            `/${community.slug}/${post?.slug}${hash}`,
          )}`,
        })
      } else {
        window.open(
          getUrlToRedirectToSSOWithReturnUrl(
            `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
              community.slug
            }/${
              post?.slug
            }?source=communities&action=embeddedarticle&campaign=${
              community.slug
            }/${post?.slug}${hash}`,
          ),
        )
      }
    }
  }
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: rem(8),
        border: `1px solid ${theme.colors.neutral[1]}`,
        padding: rem(12),
        borderRadius: rem(8),
        background: theme.colors.neutral[0],
      })}
    >
      <CardAuthor {...authorProps} avatarProps={{ size: 32 }}>
        <CardAuthor.MainText>
          {post?.expertAnswer?.expert?.name}
        </CardAuthor.MainText>
        <CardAuthor.SubTextRow>
          <CardAuthor.SubText>{post?.createdTime}</CardAuthor.SubText>
        </CardAuthor.SubTextRow>
      </CardAuthor>
      <Text
        size="p3"
        as="div"
        sx={{
          paddingLeft: '44px',
        }}
        className={classes.postContent}
      >
        <PostContent
          key={String(post?.id)}
          // trackingCategory={TRACKING_CATEGORY}
          // url={postHref}
          className="post-content-wrapper"
          description={answer?.content}
          //   disableShortenContent={disableShortenContent}
          mentions={post?.mentions}
          data-testid="Content of the Post"
          maxChars={80}
          style={{ padding: 0 }}
          hideShowAllBtn
          //   onClickCreatePostLink={handleClickCreatePost}
        />
        <a
          className={classes.linkOverlay}
          href={staticUrl}
          onClick={onClickPost}
          title={post.title}
        >
          &nbsp;
        </a>
      </Text>
    </Box>
  )
}

export const _CardPost = ({
  post,
  hasExpertComment,
  ...rest
}: CardPostProps) => {
  return (
    <Box {...rest}>
      <_CardPostItem
        post={post}
        hasExpertComment={hasExpertComment}
        style={{ width: '100%', height: '100%' }}
      >
        {hasExpertComment && post?.expertAnswer?.answer?.[0] && (
          <_CardComment post={post} />
        )}
      </_CardPostItem>
    </Box>
  )
}
